import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Card, ButtonGroup, Button } from 'react-bootstrap';
import Chart from 'react-apexcharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import SearchableSelect from '../../../components/SearchableSelect';

import { generateMessagesTrendData } from '../utils/dataGenerators';
import { getMessagesTrendChartOptions } from '../configs/chartConfigs';
import { useMessagesTrendMetrics } from '../hooks/useMetrics';

// Static message types data for Toyota
const TOYOTA_MESSAGE_TYPES_DATA = [
  { "ED": "Beacon – Scheduled Check in", "DIR": "D2C" },
  { "ED": "Checklist Result", "DIR": "D2C" },
  { "ED": "Config List Ack Message", "DIR": "D2C" },
  { "ED": "Debug Command", "DIR": "C2D" },
  { "ED": "Debug Status", "DIR": "D2C" },
  { "ED": "Firmware Update", "DIR": "C2D" },
  { "ED": "Firmware Update Response", "DIR": "D2C" },
  { "ED": "Impact", "DIR": "D2C" },
  { "ED": "Install Complete", "DIR": "D2C" },
  { "ED": "Installed Hardware", "DIR": "D2C" },
  { "ED": "Key State", "DIR": "D2C" },
  { "ED": "Location Plus", "DIR": "D2C" },
  { "ED": "Pairing Response", "DIR": "C2D" },
  { "ED": "Peripheral Connection", "DIR": "D2C" },
  { "ED": "Ping Response", "DIR": "D2C" },
  { "ED": "Ping Vehicle", "DIR": "C2D" },
  { "ED": "Remote Debug", "DIR": "C2D" },
  { "ED": "Service Level Change", "DIR": "C2D" },
  { "ED": "Service Level Change Response", "DIR": "D2C" },
  { "ED": "Service Level Status", "DIR": "D2C" },
  { "ED": "Site Swap", "DIR": "C2D" },
  { "ED": "Site Swap Response", "DIR": "D2C" },
  { "ED": "Storage Statistics", "DIR": "D2C" },
  { "ED": "Sync", "DIR": "C2D" },
  { "ED": "System Command", "DIR": "C2D" },
  { "ED": "System Reboot", "DIR": "D2C" },
  { "ED": "Telematics Error", "DIR": "D2C" },
  { "ED": "Time Correction", "DIR": "D2C" },
  { "ED": "Vehicle Error", "DIR": "D2C" },
  { "ED": "Vehicle Pairing Request", "DIR": "D2C" },
  { "ED": "WiFi Change Command", "DIR": "C2D" },
  { "ED": "WiFi Change Response", "DIR": "D2C" }
];

const ToyotaMessagesTrendChart = ({ chartFilters, selectedManufacturer = 'toyota' }) => {
  // Local state for message-specific filters
  const [direction, setDirection] = useState(null);
  const [messageTypes, setMessageTypes] = useState([]);

  // Fetch real data from API with backend filters only
  const { messagesTrendData, loading, error } = useMessagesTrendMetrics(selectedManufacturer, chartFilters);

  // Get message type options based on direction
  const messageTypeOptions = useMemo(() => {
    // If no direction selected, show all message types
    if (!direction) {
      return TOYOTA_MESSAGE_TYPES_DATA
        .filter(mt => mt.ED !== null)
        .map(mt => ({ value: mt.ED, label: mt.ED }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }

    const directionCode = direction === 'inbound' ? 'D2C' : 'C2D';
    return TOYOTA_MESSAGE_TYPES_DATA
      .filter(mt => mt.DIR === directionCode && mt.ED !== null)
      .map(mt => ({ value: mt.ED, label: mt.ED }));
  }, [direction]);

  // Clear message types when direction changes
  useEffect(() => {
    setMessageTypes([]);
  }, [direction]);

  // Transform API data to chart format with local message type and direction filtering
  const chartData = generateMessagesTrendData(messagesTrendData, messageTypes, direction, selectedManufacturer);
  const chartOptions = getMessagesTrendChartOptions(chartData);

  return (
    <Row>
      <Col>
        <Card style={{ minHeight: '500px' }}>
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center">
              <span>Vehicle Messages Trend</span>
              <div className="d-flex align-items-center">
                <span className="badge bg-secondary">last 7 days</span>
              </div>
            </div>
          </Card.Header>
          <Card.Body>
            {/* Inline Filters for Messages Trend */}
            <div className="d-flex align-items-center gap-3 mb-3 pb-2 border-bottom">
              <div className="d-flex align-items-center gap-2">
                <small className="text-muted fw-semibold">Direction:</small>
                <ButtonGroup size="sm">
                  <Button
                    variant={direction === 'inbound' ? 'primary' : 'outline-secondary'}
                    onClick={() => setDirection(direction === 'inbound' ? null : 'inbound')}
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.75rem' }}
                  >
                    Inbound
                  </Button>
                  <Button
                    variant={direction === 'outbound' ? 'primary' : 'outline-secondary'}
                    onClick={() => setDirection(direction === 'outbound' ? null : 'outbound')}
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.75rem' }}
                  >
                    Outbound
                  </Button>
                </ButtonGroup>
              </div>
              <div className="d-flex align-items-center gap-2 flex-grow-1">
                <small className="text-muted fw-semibold text-nowrap">Message Type:</small>
                <div style={{ minWidth: '250px', maxWidth: '400px' }}>
                  <SearchableSelect
                    options={messageTypeOptions}
                    value={messageTypes}
                    onChange={setMessageTypes}
                    placeholder="Filter by type..."
                    size="sm"
                    isMulti={true}
                    hideBadges={true}
                  />
                </div>
                <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                  {!direction
                    ? `All directions${messageTypes.length > 0 ? ` • ${messageTypes.length} selected` : ''}`
                    : `${direction === 'inbound' ? 'D2C' : 'C2D'}${messageTypes.length > 0 ? ` • ${messageTypes.length} selected` : ''}`
                  }
                  {messageTypes.length > 0 && <FontAwesomeIcon icon={faTimes} size="lg" style={{ cursor: 'pointer', marginLeft: '0.25rem' }} onClick={() => setMessageTypes([])} />}
                </small>
              </div>
            </div>
            {/* ApexCharts Chart */}
            {loading ? (
              <div className="d-flex align-items-center justify-content-center" style={{ height: '300px' }}>
                <span className="text-muted">Loading messages trend data...</span>
              </div>
            ) : error ? (
              <div className="d-flex align-items-center justify-content-center" style={{ height: '300px' }}>
                <span className="text-danger">Error loading data. Please try again.</span>
              </div>
            ) : chartData.series && chartData.series.length > 0 && chartData.series[0]?.data?.length > 0 ? (
              <Chart
                options={chartOptions}
                series={chartData.series}
                type="bar"
                height={350}
              />
            ) : (
              <div className="d-flex align-items-center justify-content-center" style={{ height: '300px' }}>
                <span className="text-muted">No data available</span>
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default ToyotaMessagesTrendChart;
